import { yupResolver } from "@hookform/resolvers/yup";
import { merchantRegistration, sendOtp } from "core/Services/AuthServices";
import { iMerchantRegistration } from "core/kangarooTypes/iMerhantReristration";
import { ChevronDown, Info } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "shared/Button";
import FormCheckbox from "shared/FormCheckbox";
import FormInput from "shared/FormInput";
import MapLocationWidget from "shared/MapLocationWidget";

import { ACCESS_TOKEN, USER_ROLE } from "utils/constant";
import * as yup from "yup";
interface MerchantFormData {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  termsAgree: boolean;
  coordinates?: { lat?: number; lng?: number };
  address?: string;
  addressName?: string;
  additionalAddressInfo?: string;
}

const MerchantRegistration = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { t } = useTranslation();
  const MerchantRegistrationSchema = yup.object().shape({
    companyName: yup
      .string()
      .required(t("auth.validation.companyNameRequired")),
    firstName: yup.string().required(t("auth.validation.firstNameRequired")),
    lastName: yup.string().required(t("auth.validation.lastNameRequired")),
    email: yup
      .string()
      .email(t("auth.validation.invalidEmail"))
      .required(t("auth.validation.emailRequired")),
    phoneNumber: yup
      .string()
      .required(t("auth.validation.phoneNumberRequired")),
    password: yup.string().required(t("auth.validation.passwordRequired")),
    phone: yup.string().required(t("auth.validation.countryCodeRequired")),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("password"), undefined],
        t("auth.validation.passwordsMustMatch")
      )
      .required(t("auth.validation.confirmPasswordRequired")),
    termsAgree: yup
      .boolean()
      .oneOf([true], t("auth.validation.termsAgreeRequired"))
      .required(t("auth.validation.termsAgreeRequired")),
    coordinates: yup.object().shape({
      lat: yup.number().optional(),
      lng: yup.number().optional(),
    }),
    address: yup.string().optional(),
    addressName: yup.string().optional(),
    additionalAddressInfo: yup.string().optional(),
  });
  const phones = [
    { value: "+962", label: "+962 Jordan" },
    { value: "+966", label: "+966 Saudi Arabia" },
    { value: "+971", label: "+971 United Arab Emirates" },
    { value: "+965", label: "+965 Kuwait" },
    { value: "+973", label: "+973 Bahrain" },
    { value: "+974", label: "+974 Qatar" },
    { value: "+968", label: "+968 Oman" },
    { value: "+964", label: "+964 Iraq" },
    { value: "+963", label: "+963 Syria" },
    { value: "+961", label: "+961 Lebanon" },
    { value: "+970", label: "+970 Palestine" },
    { value: "+20", label: "+20 Egypt" },
    { value: "+90", label: "+90 Turkey" },
    { value: "+98", label: "+98 Iran" },
    { value: "+967", label: "+967 Yemen" },
    { value: "+249", label: "+249 Sudan" },
  ];

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    const role = localStorage.getItem(USER_ROLE);

    if (token && role) {
      switch (role) {
        case "merchant":
          navigate("/merchant-dashboard?pageTitle=Dashboard");
          break;
        case "admin":
          navigate("/admin-dashboard?pageTitle=Dashboard");
          break;
        case "operator":
          navigate("/operator-dashboard?pageTitle=Dashboard");
          break;
        case "accountant":
          navigate("/accountant-dashboard?pageTitle=Dashboard");
          break;
        default:
          navigate("/?pageTitle=Home");
      }
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
  } = useForm<MerchantFormData>({
    resolver: yupResolver<MerchantFormData>(MerchantRegistrationSchema),
    defaultValues: {
      companyName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "+962",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      termsAgree: false,
      coordinates: { lat: 31.9515, lng: 35.9284 }, // Amman downtown coordinates
      address: "",
      addressName: "",
      additionalAddressInfo: "",
    },
  });

  const onSubmit = async (data: MerchantFormData) => {
    try {
      setIsSubmitting(true);
      const formData: iMerchantRegistration = {
        first_name: data.firstName,
        last_name: data.lastName,
        business_name: data.companyName,
        phone_number: `${data.phone}${data.phoneNumber}`,
        email: data.email,
        password: data.password,
        lat: data.coordinates?.lat?.toString(),
        long: data.coordinates?.lng?.toString(),
        address: data.additionalAddressInfo,
        address_name: data.addressName,
      };
      await merchantRegistration(formData);
      navigate(`/verify-otp/:${data.email}`);
      toast.success(t("auth.merchantRegistrationSuccess"));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t("auth.merchantRegistrationError")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationChange = (
    coordinates: { lat: number; lng: number },
    address?: string
  ) => {
    setValue("coordinates", coordinates);
    setValue("address", address);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-semibold mb-2">
          {t("auth.merchantRegistration")}
        </h1>
        <p className="text-gray-600 flex items-center justify-center gap-2">
          <Info size={20} className="text-blue-500" />
          {t("auth.createMerchantAccount")}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Company Name */}
          <FormInput
            label={t("auth.commercialName")}
            name="companyName"
            placeholder={t("auth.enterCommercialName")}
            register={register}
            errors={errors}
          />

          {/* Name Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <FormInput
              label={t("auth.firstName")}
              name="firstName"
              placeholder={t("auth.enterFirstName")}
              register={register}
              errors={errors}
            />
            <FormInput
              label={t("auth.lastName")}
              name="lastName"
              placeholder={t("auth.enterLastName")}
              register={register}
              errors={errors}
            />
          </div>

          {/* Contact Information */}
          <FormInput
            label={t("auth.emailAddress")}
            name="email"
            type="email"
            placeholder={t("auth.enterEmailAddress")}
            register={register}
            errors={errors}
          />

          <div className="flex gap-2">
            <div className="w-1/3">
              <FormInput
                label={t("auth.countryCode")}
                name="phone"
                type="select"
                register={register}
                errors={errors}
                selectOptions={phones}
                icon={<ChevronDown size={20} />}
              />
            </div>
            <div className="w-2/3">
              <FormInput
                label={t("auth.phoneNumber")}
                name="phoneNumber"
                type="text"
                placeholder={t("auth.enterPhoneNumber")}
                register={register}
                errors={errors}
              />
            </div>
          </div>

          {/* Location Widget */}
          {/* <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t("auth.businessLocation")}
            </label>
            <MapLocationWidget
              initialCoordinates={getValues("coordinates")}
              withSearch={true}
              onLocationChange={(coordinates, addressText) =>
                handleLocationChange(coordinates, addressText)
              }
              height="h-64"
            />
          </div> */}

          {/* Password Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <FormInput
              label={t("auth.password")}
              name="password"
              type="password"
              placeholder={t("auth.enterPassword")}
              register={register}
              errors={errors}
            />
            <FormInput
              label={t("auth.confirmPassword")}
              name="confirmPassword"
              type="password"
              placeholder={t("auth.confirmPassword")}
              register={register}
              errors={errors}
            />
          </div>

          {/* Terms Agreement */}
          <FormCheckbox
            label={
              <span>
                {t("auth.termsAgree")}
                <Link
                  to="/terms-of-service"
                  className="text-blue-600 hover:underline"
                >
                  {t("auth.termsOfService")}
                </Link>{" "}
                {t("common.and")}{" "}
                <Link
                  to="/privacy-policy"
                  className="text-blue-600 hover:underline"
                >
                  {t("auth.privacyPolicy")}
                </Link>
              </span>
            }
            name="termsAgree"
            register={register}
            errors={errors}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("auth.registering") : t("auth.register")}
          </Button>
        </form>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 mt-4">
        © {new Date().getFullYear()} Roots Portal. All rights reserved.
      </div>
    </div>
  );
};

export default MerchantRegistration;
